import {
  ArrowNarrowRight,
  ArrowUp,
  CheckCircle,
  Microphone02,
  Paperclip,
  Trash01,
  Upload01,
} from '@untitledui/icons';
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Button as AriaButton } from 'react-aria-components';

import type { UploadedFile } from '@/components/files';
import { getFileIcon } from '@/components/files/utils/getFileIcon';
import { Button } from '@/lib/base/buttons/button';
import { CloseButton } from '@/lib/base/buttons/close-button';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';
import { Dropdown } from '@/lib/base/overlays/dropdown';
import { PlateConversationEditor } from '@/lib/crm/record/PlateConversationEditor';
import { fileService } from '@/lib/files/service';
import { createPicker, loadGoogleApis } from '@/lib/google-picker';
import { fetchWithDefaults } from '@/lib/http/client';
import { logger } from '@/lib/logger';
import { useVoiceInput } from '@/lib/voice';

import { AttachedFilesChips, type PendingUpload } from './AttachedFilesChips';
import {
  categorizeNetworkError,
  categorizeUploadResponse,
} from './uploadErrors';

/**
 * Generate a stable id for tracking an in-flight or failed upload across the
 * lifecycle of the chip (initial attempt → error → retry → success).
 *
 * Uses crypto.randomUUID when available; falls back to a Math.random-based id
 * for older browsers / SSR contexts.
 */
function makePendingUploadId(): string {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }
  return `pending-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/** Google Drive icon SVG component */
function GoogleDriveIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox='0 0 24 24'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
    >
      <path
        d='M8.24 3H15.76L22 14.29L18.12 21H5.88L2 14.29L8.24 3Z'
        stroke='currentColor'
        strokeWidth='2'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M8.24 3L14.31 14.29H22'
        stroke='currentColor'
        strokeWidth='2'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M15.76 3L9.69 14.29H2'
        stroke='currentColor'
        strokeWidth='2'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  );
}

type ConversationInputVariant = 'default' | 'simple' | 'hero';

// Auto-submit + stop recording after this many ms of no new voice transcript
// activity while recording. Hoisted per FRONTEND_PATTERNS.md "Hoist Constants".
const SILENCE_AUTO_SEND_MS = 5000;

/**
 * Whether a variant renders per-file upload failures as inline chips (via
 * `AttachedFilesChips`) rather than the shared error banner. Single source of
 * truth for both the compact-style layout switch and the upload-error routing,
 * so a new chip-rendering variant can't render a chip while still leaking the
 * same failure to the banner (a duplicate error surface).
 */
function variantRendersFileChips(variant: ConversationInputVariant): boolean {
  return variant === 'simple' || variant === 'hero';
}

export interface ConversationInputProps {
  /** Current message value */
  value: string;
  /** Callback when message changes */
  onChange: (value: string) => void;
  /** Callback when form is submitted */
  onSubmit: (e: React.FormEvent) => void;
  /** Placeholder text */
  placeholder?: string;
  /** Loading state - disables input and submit */
  loading?: boolean;
  /** Processing state - shows "AI is thinking..." indicator */
  isProcessing?: boolean;
  /** Error message to display */
  error?: string | null;
  /**
   * Styling variant:
   * - 'default': tall box, bottom controls, Button-style send
   * - 'simple': compact single-line, top controls, black-square send (Actions page)
   * - 'hero': landing/conversation box — tall with bottom controls but the
   *   compact black-square send + inline file chips (see `isCompactStyle`)
   */
  variant?: ConversationInputVariant;
  /** Org ID - required for file upload */
  orgId?: number;
  /** Uploaded files list */
  uploadedFiles?: UploadedFile[];
  /** Callback when files change */
  onFilesChange?: (files: UploadedFile[]) => void;
  /** Whether the input should be sticky at bottom */
  sticky?: boolean;
  /** Additional CSS classes for the container */
  className?: string;
  /** Callback when input expands/collapses (simple variant only) */
  onExpandedChange?: (expanded: boolean) => void;
  /** Whether to auto-focus the input on mount */
  autoFocus?: boolean;
  /** Whether to keep the input compact until text wraps (simple variant only, default: false) */
  compactUntilWrap?: boolean;
  /** Whether to show voice input button (default: true for non-simple variants) */
  enableVoice?: boolean;
}

/**
 * Displays a generic submission, voice, or aggregated upload error in the
 * banner below the input.
 *
 * Per-file upload failures are rendered as inline chips by `AttachedFilesChips`
 * with their own retry/dismiss affordances; this banner only fires when the
 * error is not attributable to a single file (e.g. a multi-file Drive import
 * partial failure, voice connectivity issue, or a submission-time error).
 */
function ErrorDisplay({
  error: err,
  uploadError: uploadErr,
  voiceError: voiceErr,
  onDismissUploadError,
}: {
  error: string | null;
  uploadError: string | null;
  voiceError: string | null;
  onDismissUploadError: () => void;
}) {
  const isUploadError = !err && !voiceErr && !!uploadErr;
  const message = err || uploadErr || voiceErr || '';

  return (
    <div className='flex items-start justify-between gap-2'>
      <div className='text-xs text-[var(--color-error-700)] dark:text-[var(--color-error-300)]'>
        {message}
      </div>
      {isUploadError && (
        <CloseButton
          onClick={onDismissUploadError}
          theme='light'
          size='xs'
          label='Dismiss upload error'
        />
      )}
    </div>
  );
}

function joinVoiceSegment(base: string, seg: string): string {
  const trimmed = seg.trim();
  if (base.length === 0) {
    return trimmed;
  }
  if (/\s$/.test(base)) {
    return base + trimmed;
  }
  return base + ' ' + trimmed;
}

export function ConversationInput({
  value,
  onChange,
  onSubmit,
  placeholder = 'Message Ahoy',
  loading = false,
  isProcessing = false,
  error = null,
  variant = 'default',
  orgId,
  uploadedFiles = [],
  onFilesChange,
  sticky = true,
  className = '',
  onExpandedChange,
  autoFocus = false,
  compactUntilWrap = false,
  enableVoice: enableVoiceProp,
}: ConversationInputProps) {
  // 'hero' shares the compact black-square send + inline file-chip treatment
  // with 'simple', but keeps the tall/bottom-control box layout of the
  // default variant. This flag gates the *control styling / file handling*;
  // positional ternaries stay keyed on `variant === 'simple'` so hero falls
  // to the bottom-control side automatically.
  const isCompactStyle = variantRendersFileChips(variant);
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  // Per-file upload tracking. Each entry represents a single file that is
  // either currently uploading (status: 'uploading') or has failed and is
  // awaiting retry/dismiss (status: 'error'). Successful uploads are removed
  // from this list and added to `uploadedFiles` (managed by the parent).
  const [pendingUploads, setPendingUploads] = useState<PendingUpload[]>([]);
  // The original `File` objects, keyed by clientId, so retries can re-submit
  // without asking the user to re-pick from disk. Lives outside React state
  // because Files are non-serializable and only consumed inside callbacks.
  const pendingFilesRef = useRef<Map<string, File>>(new Map());
  // Track the current variant for use inside upload callbacks. Chip-rendering
  // variants (see `variantRendersFileChips`) surface per-file errors as chips;
  // the remaining variants have no chip strip, so per-file errors must also
  // fall through to the banner. Reading from a ref avoids invalidating the
  // upload callback every time the variant prop changes.
  const variantRef = useRef(variant);
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.innerWidth < 768
  );
  // Google Drive import state
  const [driveImporting, setDriveImporting] = useState(false);
  const [driveImportStatus, setDriveImportStatus] = useState<string | null>(
    null
  );
  const [driveConnectionError, setDriveConnectionError] = useState(false);
  const [voiceError, setVoiceError] = useState<string | null>(null);
  const [fileAnnouncement, setFileAnnouncement] = useState<string>('');
  const [voiceStartIndex, setVoiceStartIndex] = useState<number | null>(null);
  const editorWrapperRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  // Ref to track latest uploadedFiles for concurrent uploads
  const uploadedFilesRef = useRef<UploadedFile[]>(uploadedFiles);
  // Bumped on every direct write to `uploadedFilesRef` inside
  // runUploadAttempt's success path. Concurrent uploads can complete such
  // that a render carrying an older `uploadedFiles` prop value commits (and
  // its resync effect below runs) *after* a later upload has already
  // advanced the ref directly. Paired with `uploadedFilesGenerationByArray`
  // below, this lets that effect detect the staleness instead of clobbering
  // the newer value — keyed by array identity rather than "version as of
  // render time" so the check is correct regardless of how long the render
  // carrying a given `uploadedFiles` value took to commit. See the resync
  // effect.
  const uploadedFilesVersionRef = useRef(0);
  const uploadedFilesGenerationByArray = useRef(
    new WeakMap<UploadedFile[], number>()
  );
  // Refs to track latest value/voiceStartIndex for voice transcript callback
  // (avoids stale closure when WebSocket delivers transcript events)
  const valueRef = useRef(value);
  const voiceStartIndexRef = useRef(voiceStartIndex);
  const prevFilesLengthRef = useRef(uploadedFiles.length);

  const enableFileUpload = !!orgId && !!onFilesChange;
  const enableVoice = enableVoiceProp ?? variant !== 'simple';
  const attachStatusId = `attach-button-status-${useId()}`;

  // Keep refs in sync with current values
  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  useEffect(() => {
    voiceStartIndexRef.current = voiceStartIndex;
  }, [voiceStartIndex]);

  // Handle editor submit (Enter without Shift)
  const handleEditorSubmit = useCallback(() => {
    const trimmedValue = value.trim();
    if (trimmedValue && !loading) {
      onChange(trimmedValue);
      // Create a synthetic form event for the onSubmit callback
      const syntheticEvent = {
        preventDefault: () => {},
        stopPropagation: () => {},
        currentTarget: formRef.current,
        target: formRef.current,
        nativeEvent: new Event('submit'),
        bubbles: true,
        cancelable: true,
        defaultPrevented: false,
        eventPhase: 0,
        isTrusted: false,
        timeStamp: Date.now(),
        type: 'submit',
        isDefaultPrevented: () => false,
        isPropagationStopped: () => false,
        persist: () => {},
      } as unknown as React.FormEvent;
      onSubmit(syntheticEvent);
    }
  }, [value, loading, onChange, onSubmit]);

  const silenceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Latest fire action, refreshed each render (below) so the 5s timer never
  // submits stale `value`/`loading`. Mirrors the file's valueRef/voiceStartIndexRef idiom.
  const autoSendRef = useRef<() => void>(() => {});

  const clearSilenceTimer = useCallback(() => {
    if (silenceTimerRef.current !== null) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
  }, []);

  const armSilenceTimer = useCallback(() => {
    clearSilenceTimer();
    silenceTimerRef.current = setTimeout(() => {
      // Null the ref BEFORE firing so the status-cancel effect can't race the
      // in-flight fire.
      silenceTimerRef.current = null;
      autoSendRef.current();
    }, SILENCE_AUTO_SEND_MS);
  }, [clearSilenceTimer]);

  // Editor onChange wrapper: a genuine user edit cancels the pending auto-send.
  // (Voice updates go through the raw `onChange` in handleVoiceTranscript and
  // never reach here, because PlateConversationEditor suppresses external-value echoes.)
  const handleEditorChange = useCallback(
    (next: string) => {
      clearSilenceTimer();
      onChange(next);
    },
    [clearSilenceTimer, onChange]
  );

  // Voice input callback - uses refs to avoid stale closure
  const handleVoiceTranscript = useCallback(
    (text: string, isFinal: boolean) => {
      if (!isFinal && text.length === 0) {
        return;
      }
      const currentValue = valueRef.current;
      const rawAnchor = voiceStartIndexRef.current;
      const anchor =
        rawAnchor === null ? null : Math.min(rawAnchor, currentValue.length);
      const committedBase =
        anchor !== null ? currentValue.slice(0, anchor) : currentValue;
      if (isFinal) {
        onChange(joinVoiceSegment(committedBase, text));
        setVoiceStartIndex(null);
        setVoiceError(null);
      } else if (anchor === null) {
        const needsSep = committedBase.length > 0 && !/\s$/.test(committedBase);
        const nextBase = needsSep ? committedBase + ' ' : committedBase;
        setVoiceStartIndex(nextBase.length);
        onChange(nextBase + text.trimStart());
      } else {
        onChange(committedBase + text.trimStart());
      }
      if (text.trim().length > 0) {
        armSilenceTimer();
      }
    },
    [onChange, armSilenceTimer]
  );

  // Voice input hook
  const {
    status: voiceStatus,
    error: voiceHookError,
    startRecording,
    stopRecording,
    isSupported: isVoiceSupported,
  } = useVoiceInput({
    orgId: orgId ?? null,
    onTranscript: handleVoiceTranscript,
  });

  // Keep the fire action current (fresh value/loading via handleEditorSubmit).
  useEffect(() => {
    autoSendRef.current = () => {
      // handleEditorSubmit self-guards on trimmed value && !loading, so it never
      // submits empty text and never double-submits while a send is in flight.
      handleEditorSubmit();
      // One-shot: always release the mic afterward, even when submit was skipped.
      stopRecording();
      setVoiceStartIndex(null);
    };
  }, [handleEditorSubmit, stopRecording]);

  // Cancel any pending auto-send the moment we leave the recording state
  // (manual stop -> idle, or error). No delayed submit should fire afterward.
  useEffect(() => {
    if (voiceStatus !== 'recording') {
      clearSilenceTimer();
    }
  }, [voiceStatus, clearSilenceTimer]);

  // Cancel on unmount.
  useEffect(() => clearSilenceTimer, [clearSilenceTimer]);

  // Sync voice error with hook error (including clearing when null)
  useEffect(() => {
    setVoiceError(voiceHookError);
  }, [voiceHookError]);

  // For simple variant, expand only when there's content (not just on focus)
  const isExpanded = variant === 'simple' && value.trim().length > 0;

  // Keep ref in sync with prop — but skip if a concurrent direct write (see
  // runUploadAttempt) has advanced the ref past what this specific
  // `uploadedFiles` array reflects. Looking the generation up by array
  // identity (rather than reading "whatever uploadedFilesVersionRef says
  // right now" during render) means the check is correct no matter how long
  // this render took to reach commit — a value produced at generation 1 is
  // still recognized as generation 1 even if its render committed after
  // generation 2 already landed. Arrays this component didn't produce itself
  // (e.g. the parent removing a file) have no entry and always sync, which
  // is exactly the legitimate-removal case.
  useEffect(() => {
    const propGeneration =
      uploadedFilesGenerationByArray.current.get(uploadedFiles);
    if (
      propGeneration !== undefined &&
      propGeneration < uploadedFilesVersionRef.current
    ) {
      logger.debug(
        'Skipped stale uploadedFiles resync: a newer direct write already advanced the ref',
        {
          component: 'conversation-input',
          propFileCount: uploadedFiles.length,
          refFileCount: uploadedFilesRef.current.length,
          propGeneration,
          currentGeneration: uploadedFilesVersionRef.current,
        }
      );
      return;
    }
    uploadedFilesRef.current = uploadedFiles;
  }, [uploadedFiles]);

  useEffect(() => {
    variantRef.current = variant;
  }, [variant]);

  // Announce file attachment/removal to screen readers
  useEffect(() => {
    const prev = prevFilesLengthRef.current;
    const next = uploadedFiles.length;
    prevFilesLengthRef.current = next;
    if (next > prev) {
      const latestFile = uploadedFiles[uploadedFiles.length - 1];
      setFileAnnouncement(`File attached: ${latestFile.original_filename}`);
    } else if (next < prev) {
      setFileAnnouncement('File removed');
    }
    if (next !== prev) {
      const timer = setTimeout(() => setFileAnnouncement(''), 2000);
      return () => clearTimeout(timer);
    }
  }, [uploadedFiles]);

  // Notify parent when expanded state changes
  useEffect(() => {
    onExpandedChange?.(isExpanded);
  }, [isExpanded, onExpandedChange]);

  // Track viewport size for responsive layout
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedValue = value.trim();
    if (trimmedValue && !loading) {
      onChange(trimmedValue);
      onSubmit(e);
    }
  };

  /**
   * Run an upload attempt for a file already tracked in `pendingUploads`.
   *
   * On success: removes the pending entry and appends to `uploadedFiles`.
   * On failure: flips the pending entry's status to `'error'` and stores the
   * categorized error info so the chip can show `title` + `message` + retry.
   *
   * Multiple uploads can run concurrently — each owns its own `clientId` and
   * the pending list is a stable, additive data structure (entries are
   * mutated in place by clientId, never overwritten).
   */
  const runUploadAttempt = useCallback(
    async (clientId: string, file: File) => {
      if (!orgId || !onFilesChange) {
        return;
      }

      setUploading(true);

      try {
        const formData = new FormData();
        formData.append('file', file);

        const response = await fetchWithDefaults(`/api/orgs/${orgId}/files/`, {
          method: 'POST',
          body: formData,
        });

        if (!response.ok) {
          const info = await categorizeUploadResponse(response);
          // Log only metadata — never the body — so we can diagnose backend
          // regressions without leaking PII into the console.
          console.error('Upload rejected by server', {
            status: response.status,
            statusText: response.statusText,
            contentType: response.headers.get('content-type'),
            category: info.category,
            message: info.message,
            clientId,
          });
          setPendingUploads(current =>
            current.map(entry =>
              entry.clientId === clientId
                ? {
                    ...entry,
                    status: 'error' as const,
                    errorTitle: info.title,
                    errorMessage: info.title,
                    errorCategory: info.category,
                  }
                : entry
            )
          );
          // Variants without a chip strip route the error to the banner as
          // well. Chip-rendering variants only show the chip to avoid a
          // duplicate banner + chip for the same file.
          if (!variantRendersFileChips(variantRef.current)) {
            setUploadError(info.title);
          }
          return;
        }

        const uploadedFile: UploadedFile = await response.json();
        // Use ref to get latest files, avoiding stale closure in concurrent uploads
        // Update ref immediately so subsequent concurrent uploads see the latest state
        const nextFiles = [...uploadedFilesRef.current, uploadedFile];
        uploadedFilesRef.current = nextFiles;
        uploadedFilesVersionRef.current += 1;
        uploadedFilesGenerationByArray.current.set(
          nextFiles,
          uploadedFilesVersionRef.current
        );
        onFilesChange(nextFiles);
        setPendingUploads(current =>
          current.filter(entry => entry.clientId !== clientId)
        );
        pendingFilesRef.current.delete(clientId);
      } catch (err) {
        const info = categorizeNetworkError(err);
        setPendingUploads(current =>
          current.map(entry =>
            entry.clientId === clientId
              ? {
                  ...entry,
                  status: 'error' as const,
                  errorTitle: info.title,
                  errorMessage: info.title,
                  errorCategory: info.category,
                }
              : entry
          )
        );
        if (!variantRendersFileChips(variantRef.current)) {
          setUploadError(info.title);
        }
      } finally {
        setUploading(false);
      }
    },
    [orgId, onFilesChange]
  );

  /** Kick off an upload for a freshly-picked file (from disk or drag-drop). */
  const uploadFile = useCallback(
    (file: File) => {
      if (!orgId || !onFilesChange) {
        return;
      }
      const clientId = makePendingUploadId();
      pendingFilesRef.current.set(clientId, file);
      setPendingUploads(current => [
        ...current,
        {
          clientId,
          fileName: file.name,
          fileSize: file.size,
          status: 'uploading',
        },
      ]);
      void runUploadAttempt(clientId, file);
    },
    [orgId, onFilesChange, runUploadAttempt]
  );

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target;
    if (files && files.length > 0) {
      Array.from(files).forEach(file => uploadFile(file));
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleLocalFileClick = useCallback(() => {
    if (enableFileUpload && fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, [enableFileUpload]);

  const handleDriveFilesSelected = useCallback(
    async (docs: GooglePicker.Document[]) => {
      if (!orgId || !onFilesChange || docs.length === 0) {
        return;
      }

      const fileIds = docs.map(doc => doc.id);
      setDriveImporting(true);
      setDriveImportStatus(
        `Importing ${fileIds.length} file${fileIds.length > 1 ? 's' : ''}…`
      );
      setUploadError(null);
      setDriveConnectionError(false);

      try {
        const result = await fileService.importFromDrive(orgId, fileIds);

        // Add successful files to state
        if (result.files.length > 0) {
          const nextFiles = [...uploadedFilesRef.current, ...result.files];
          uploadedFilesRef.current = nextFiles;
          uploadedFilesVersionRef.current += 1;
          uploadedFilesGenerationByArray.current.set(
            nextFiles,
            uploadedFilesVersionRef.current
          );
          onFilesChange(nextFiles);
        }

        // Show errors for files that failed
        if (result.errors.length > 0) {
          console.error(
            '[ConversationInput] Some Drive files failed to import',
            {
              failedCount: result.errors.length,
              failures: result.errors.map(e => ({
                fileId: e.file_id,
                error: e.error,
              })),
            }
          );
          setUploadError(
            `${result.errors.length} file${result.errors.length > 1 ? 's' : ''} couldn't be imported. Try again.`
          );
        }
      } catch (err) {
        setUploadError("We couldn't import those files from Google Drive.");
        console.error(err);
      } finally {
        setDriveImporting(false);
        setDriveImportStatus(null);
      }
    },
    [orgId, onFilesChange]
  );

  const handleReconnectDrive = useCallback(() => {
    window.location.href = '/accounts/google/login/?process=connect';
  }, []);

  const handleDismissDriveError = useCallback(() => {
    setDriveConnectionError(false);
    setUploadError(null);
  }, []);

  const handleDismissUploadError = useCallback(() => {
    setUploadError(null);
  }, []);

  const handleDriveClick = useCallback(async () => {
    if (!orgId) {
      return;
    }

    setUploadError(null);
    setDriveConnectionError(false);

    try {
      // Load Google APIs
      await loadGoogleApis();

      // Fetch OAuth token from backend
      const tokenResponse = await fetchWithDefaults(
        `/api/orgs/${orgId}/files/drive/token/`
      );
      if (!tokenResponse.ok) {
        setDriveConnectionError(true);
        return;
      }

      const tokenData = await tokenResponse.json();
      const oauthToken = tokenData.access_token;

      // Create and show the picker
      const picker = createPicker({
        oauthToken,
        callback: (data: GooglePicker.ResponseObject) => {
          if (
            data.action === window.google?.picker.Action.PICKED &&
            data.docs
          ) {
            handleDriveFilesSelected(data.docs);
          }
        },
        multiSelect: true,
        title: 'Select files from Google Drive',
      });

      if (picker) {
        picker.setVisible(true);
      } else {
        // createPicker returns null when API key is not configured
        setUploadError(
          'Google Drive picker is not configured. Please contact your administrator.'
        );
      }
    } catch (err) {
      setUploadError("We couldn't open the Google Drive picker.");
      console.error(err);
    }
  }, [orgId, handleDriveFilesSelected]);

  const handleAttachAction = useCallback(
    (key: React.Key) => {
      if (key === 'local') {
        handleLocalFileClick();
      } else if (key === 'drive') {
        handleDriveClick();
      }
    },
    [handleLocalFileClick, handleDriveClick]
  );

  const handleFileDelete = useCallback(
    (fileId: number) => {
      if (onFilesChange) {
        const nextFiles = uploadedFilesRef.current.filter(f => f.id !== fileId);
        uploadedFilesRef.current = nextFiles;
        uploadedFilesVersionRef.current += 1;
        uploadedFilesGenerationByArray.current.set(
          nextFiles,
          uploadedFilesVersionRef.current
        );
        onFilesChange(nextFiles);
      }
    },
    [onFilesChange]
  );

  /** Re-run a failed upload using the cached `File` for the given `clientId`. */
  const handleRetryUpload = useCallback(
    (clientId: string) => {
      const file = pendingFilesRef.current.get(clientId);
      if (!file) {
        return;
      }
      // Clear any previous banner error so the user sees a fresh attempt.
      setUploadError(null);
      setPendingUploads(current =>
        current.map(entry =>
          entry.clientId === clientId
            ? {
                ...entry,
                status: 'uploading' as const,
                errorTitle: undefined,
                errorMessage: undefined,
                errorCategory: undefined,
              }
            : entry
        )
      );
      void runUploadAttempt(clientId, file);
    },
    [runUploadAttempt]
  );

  /** Drop a pending or failed upload entirely (cancel / acknowledge error). */
  const handleDismissPendingUpload = useCallback((clientId: string) => {
    pendingFilesRef.current.delete(clientId);
    setPendingUploads(current =>
      current.filter(entry => entry.clientId !== clientId)
    );
  }, []);

  const handleFileCardDelete = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      const { fileId } = e.currentTarget.dataset;
      if (fileId !== undefined) {
        handleFileDelete(Number(fileId));
      }
    },
    [handleFileDelete]
  );

  const focusEditor = useCallback(() => {
    const editor = editorWrapperRef.current?.querySelector<HTMLElement>(
      '[contenteditable], textarea, [role="textbox"]'
    );
    editor?.focus();
  }, []);

  const handleVoiceClick = useCallback(() => {
    if (voiceStatus === 'recording') {
      stopRecording();
      setVoiceStartIndex(null);
    } else {
      startRecording();
    }
  }, [voiceStatus, stopRecording, setVoiceStartIndex, startRecording]);

  // Drag and drop handlers
  const handleDragEnter = useCallback(
    (e: React.DragEvent) => {
      if (!enableFileUpload) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(true);
    },
    [enableFileUpload]
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      if (!enableFileUpload) {
        return;
      }
      const { files } = e.dataTransfer;
      if (files && files.length > 0) {
        Array.from(files).forEach(file => uploadFile(file));
      }
    },
    [enableFileUpload, uploadFile]
  );

  // Calculate container height to accommodate absolutely positioned file cards
  // (only the tall default/gradient variants use that layout; compact-style
  // variants render inline chips and size themselves).
  const calculateContainerHeight = () => {
    if (isCompactStyle || uploadedFiles.length === 0) {
      return 'auto';
    }

    const editorHeight =
      editorWrapperRef.current?.scrollHeight || (isMobile ? 80 : 120);
    const toolbarHeight = 8;
    const topPadding = 7;
    const bottomPadding = 21;

    if (isMobile) {
      const rows = uploadedFiles.length;
      const lastRowTop =
        editorHeight + toolbarHeight + topPadding + (rows - 1) * 34;
      const fileCardHeight = 32;
      return `${lastRowTop + fileCardHeight + bottomPadding}px`;
    } else {
      const rows = Math.ceil(uploadedFiles.length / 2);
      const lastRowTop =
        editorHeight + toolbarHeight + topPadding + (rows - 1) * 84;
      const fileCardHeight = 72;
      return `${lastRowTop + fileCardHeight + bottomPadding}px`;
    }
  };

  let containerClasses: string;
  if (variant === 'simple') {
    containerClasses =
      'bg-bg-primary border border-border-secondary shadow-[0px_4px_14px_0px_rgba(150,199,221,0.8),0px_1px_2px_0px_rgba(10,13,18,0.05)] dark:shadow-[0px_4px_14px_0px_rgba(150,199,221,0.15),0px_1px_2px_0px_rgba(0,0,0,0.1)]';
  } else if (variant === 'hero') {
    // Clean opaque box — the wave renders behind it (in ConversationsHome),
    // so the background must be opaque to hide the wave's top portion.
    containerClasses =
      'bg-bg-primary border border-border-secondary shadow-[0px_1px_2px_0px_rgba(10,13,18,0.06)]';
  } else {
    containerClasses =
      'bg-gray-50 dark:bg-gray-800 ring-1 ring-gray-200 dark:ring-gray-700 ring-inset shadow-[0px_1px_2px_rgba(10,13,18,0.05)]';
  }

  const stickyClasses = sticky ? 'sticky bottom-0 z-10' : '';

  return (
    <div className={`${stickyClasses} ${className}`}>
      <div
        className={sticky ? 'max-w-4xl mx-auto px-3 sm:px-4 lg:px-6 pb-1' : ''}
      >
        <form
          ref={formRef}
          onSubmit={handleFormSubmit}
          className={`relative flex flex-col rounded-xl overflow-visible outline-none ${containerClasses} ${
            isDragging ? 'ring-2 ring-utility-brand-300' : ''
          }`}
          style={{ minHeight: calculateContainerHeight() }}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          {/* Hidden file input */}
          {enableFileUpload && (
            <input
              ref={fileInputRef}
              type='file'
              multiple
              onChange={handleFileSelect}
              className='hidden'
              accept='image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.zip,.tar,.gz'
              data-testid='file-input'
              data-component='file-input'
              data-instance='conversation-attachment'
            />
          )}

          {/* AI Processing indicator - only for the tall default/gradient variants */}
          {isProcessing && !isCompactStyle && (
            <div className='px-3 pt-3 flex items-center gap-2 text-xs sm:text-sm text-utility-brand-600'>
              <LoadingIndicator size='xs' />
              <span>AI is thinking…</span>
            </div>
          )}

          {/* Drag overlay */}
          {isDragging && (
            <div className='absolute inset-0 z-10 flex items-center justify-center bg-utility-brand-600/10 rounded-xl pointer-events-none'>
              <div className='text-utility-brand-600 font-medium text-sm'>
                Drop files to upload
              </div>
            </div>
          )}

          {/* Editor with submit button */}
          <div
            ref={editorWrapperRef}
            className={`relative flex items-stretch ${
              // Hero box height lives here (a plain flex wrapper) rather than on
              // the editor: Plate sets an inline min-height that overrides the
              // editor's `min-h-*` class. items-stretch makes the editor fill
              // this wrapper's height.
              variant === 'hero' ? 'min-h-[120px]' : ''
            }`}
          >
            <PlateConversationEditor
              value={value}
              onChange={handleEditorChange}
              placeholder={placeholder}
              readOnly={loading || isProcessing}
              variant='multi-line'
              onSubmit={handleEditorSubmit}
              autoFocus={autoFocus}
              className={`w-full rounded-xl bg-bg-primary text-fg-primary placeholder:text-fg-quaternary focus:outline-none focus:ring-0 border-0 ${(() => {
                if (variant === 'hero') {
                  // Full-width text: controls sit in the bottom padding band
                  // (pb-[44px]), so — unlike the default variant — no right
                  // reservation is needed; the text spans the whole box width.
                  return 'px-[14px] pt-[12px] pb-[44px] text-base leading-[24px]';
                }
                if (variant === 'simple') {
                  // py-[11px] perfectly centers the 22px line-height inside the
                  // 44px min-height box (11 + 22 + 11 = 44). Previously py-[10px]
                  // left a 2px deficit below the empty-state placeholder because
                  // block-level content fills the content area from the top —
                  // any leftover slack ends up below the text/placeholder, but
                  // 24px content area with a 22px line leaves only 2px slack
                  // visible as inconsistent above/below gaps. With py-[11px] the
                  // content area is exactly 22px, eliminating the asymmetry.
                  return `${enableFileUpload ? 'pl-[44px]' : 'px-[12px]'} py-[11px] ${enableVoice && isVoiceSupported ? 'pr-[82px]' : 'pr-[44px]'} text-sm leading-[22px]`;
                }
                if (enableVoice) {
                  return 'px-[14px] pt-[12px] text-base leading-[24px] pb-[44px] pr-[100px]';
                }
                if (enableFileUpload) {
                  return 'px-[14px] pt-[12px] text-base leading-[24px] pb-[44px] pr-[50px]';
                }
                return 'px-[14px] pt-[12px] text-base leading-[24px] pb-[12px] pr-[50px]';
              })()} ${(() => {
                // Note: hero height is set on the editor *wrapper*, not here —
                // Plate overrides this element's min-height via an inline style.
                if (variant !== 'simple') {
                  return 'min-h-[120px]';
                }
                if (compactUntilWrap || !value.trim()) {
                  return 'min-h-[44px]';
                }
                return 'min-h-[105px]';
              })()}`}
            />
            {/* Attach button dropdown */}
            {enableFileUpload && (
              <div
                className={`absolute z-10 ${variant === 'simple' ? 'top-[6px] left-[6px]' : 'bottom-[12px] left-[14px]'}`}
              >
                {/* aria-description: visually-hidden span used via aria-describedby since
                    react-aria filters aria-description from the DOM */}
                {(uploading || driveImporting) && (
                  <span id={attachStatusId} className='sr-only'>
                    {uploading
                      ? 'File upload in progress'
                      : 'Google Drive import in progress'}
                  </span>
                )}
                <Dropdown.Root>
                  <AriaButton
                    className={
                      variant === 'simple'
                        ? 'flex items-center justify-center size-[32px] rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 outline-focus-ring'
                        : 'h-[26px] flex items-center gap-1 text-xs font-semibold text-gray-600 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer'
                    }
                    isDisabled={uploading || driveImporting}
                    aria-label='Attach files'
                    aria-describedby={
                      uploading || driveImporting ? attachStatusId : undefined
                    }
                  >
                    <Paperclip
                      className={
                        variant === 'simple'
                          ? 'size-[18px] text-gray-500 dark:text-gray-400'
                          : 'size-5'
                      }
                      strokeWidth={2}
                    />
                    {variant !== 'simple' && (
                      <span>
                        {driveImportStatus ||
                          (uploading ? 'Uploading…' : 'Attach')}
                      </span>
                    )}
                  </AriaButton>
                  <Dropdown.Popover
                    placement={
                      variant === 'simple' ? 'bottom left' : 'top left'
                    }
                  >
                    <Dropdown.Menu onAction={handleAttachAction}>
                      <Dropdown.Item
                        id='local'
                        icon={Upload01}
                        label='Upload from computer'
                      />
                      <Dropdown.Item
                        id='drive'
                        icon={GoogleDriveIcon}
                        label='Google Drive'
                      />
                    </Dropdown.Menu>
                  </Dropdown.Popover>
                </Dropdown.Root>
              </div>
            )}
            {/* Microphone button */}
            {enableVoice &&
              isVoiceSupported &&
              (() => {
                const ariaLabel =
                  voiceStatus === 'recording'
                    ? 'Stop recording'
                    : 'Start voice input';

                return (
                  <div
                    className={`absolute z-10 ${
                      variant === 'simple'
                        ? 'top-[6px] right-[44px]'
                        : 'bottom-[12px] right-[65px]'
                    }`}
                  >
                    <button
                      type='button'
                      onClick={handleVoiceClick}
                      disabled={voiceStatus === 'connecting'}
                      aria-label={ariaLabel}
                      aria-pressed={
                        voiceStatus === 'recording' ? true : undefined
                      }
                      className={`${variant === 'simple' ? 'size-[32px]' : 'size-[26px]'} border-2 flex items-center justify-center rounded-md transition-colors cursor-pointer ${
                        voiceStatus === 'recording'
                          ? 'bg-red-500 border-red-500 animate-pulse'
                          : 'border-transparent hover:bg-gray-100 dark:hover:bg-gray-700 focus-visible:outline-2 focus-visible:outline-offset-2 outline-focus-ring'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      <Microphone02
                        className={`size-5 ${
                          voiceStatus === 'recording'
                            ? 'stroke-white'
                            : 'stroke-gray-500'
                        }`}
                        strokeWidth={2}
                      />
                    </button>
                  </div>
                );
              })()}
            {/* Submit button */}
            <div
              className={`absolute z-10 ${
                variant === 'simple'
                  ? 'top-[6px] right-[6px]'
                  : 'bottom-[8px] right-[16px]'
              }`}
            >
              {isCompactStyle ? (
                // While `isProcessing` the button shows a spinner — work is in
                // flight (round-trip + backend job), not a streamed reply, so
                // a spinner reads more accurately than a typing-style dots
                // indicator. The slide-up AgentResponsePanel in
                // ProposalDetailPanel is the canonical surface for *what* the
                // agent is doing; the spinner just signals *that* something is.
                <button
                  type={isProcessing ? 'button' : 'submit'}
                  disabled={!isProcessing && (!value.trim() || loading)}
                  aria-label={
                    isProcessing ? 'AI is processing' : 'Send message'
                  }
                  title={isProcessing ? 'AI is processing…' : 'Send message'}
                  className={`flex items-center justify-center size-[32px] rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer ${
                    variant === 'hero'
                      ? // Landing hero: solid black send with a white up-arrow.
                        'bg-black hover:bg-black/90'
                      : 'bg-[var(--color-bg-brand-solid)] hover:bg-[var(--color-bg-brand-solid_hover)]'
                  }`}
                >
                  {(() => {
                    if (isProcessing) {
                      // Override --color-fg-brand-primary scoped to this span so
                      // the LoadingIndicator's stroke renders white against the
                      // filled button (the indicator hardcodes that token on its
                      // SVG; otherwise we'd get brand-on-brand = invisible).
                      return (
                        <span className='[--color-fg-brand-primary:#fff]'>
                          <LoadingIndicator
                            size='2xs'
                            type='line-spinner'
                            inline
                          />
                        </span>
                      );
                    }
                    return variant === 'hero' ? (
                      <ArrowUp size={18} />
                    ) : (
                      <ArrowNarrowRight size={18} />
                    );
                  })()}
                </button>
              ) : (
                <Button
                  color='brand-soft'
                  type='submit'
                  isDisabled={!value.trim() || loading || isProcessing}
                  noTextPadding
                  px='xs'
                  py='sm'
                  aria-label='Send message'
                  title='Send message'
                  iconLeading={ArrowNarrowRight}
                  iconLeadingClassName='size-[18px]'
                />
              )}
            </div>
          </div>

          {/* Toolbar - Below textarea, above files - only for tall default/gradient */}
          {!isCompactStyle && (
            <div
              className={`flex w-full items-center gap-3 px-3 ${uploadedFiles.length > 0 ? 'min-h-[8px]' : 'min-h-[32px]'}`}
            />
          )}

          {/* Attached Files List - Absolutely positioned, side-by-side layout - default variant only */}
          {!isCompactStyle && uploadedFiles.length > 0 && (
            <>
              {uploadedFiles.map((file, index) => {
                const editorHeight =
                  editorWrapperRef.current?.scrollHeight ||
                  (isMobile ? 80 : 120);

                let leftPosition: string | number;
                let topPosition: number;
                let cardWidth: string | number;

                const toolbarHeight = 8;
                if (isMobile) {
                  leftPosition = '8px';
                  const row = index;
                  topPosition = editorHeight + toolbarHeight + 7 + row * 34;
                  cardWidth = 'calc(100% - 16px)';
                } else {
                  const column = index % 2;
                  const row = Math.floor(index / 2);
                  leftPosition = column === 0 ? 12 : 367;
                  topPosition = editorHeight + toolbarHeight + 7 + row * 84;
                  cardWidth = '343px';
                }

                return (
                  <div
                    key={file.id}
                    className='absolute bg-bg-primary border border-border-secondary rounded-lg p-1.5 md:p-4 md:rounded-xl flex items-center gap-2 md:gap-3'
                    style={{
                      left: leftPosition,
                      top: `${topPosition}px`,
                      width: cardWidth,
                      height: isMobile ? '32px' : 'auto',
                    }}
                  >
                    {/* File type icon */}
                    <div className='shrink-0 size-5 md:size-10 relative'>
                      {getFileIcon(file.original_filename, file.mime_type, {
                        size: isMobile ? 20 : 40,
                      })}
                    </div>

                    {/* File details */}
                    <div className='flex-1 min-w-0'>
                      <p className='text-xs md:text-sm font-medium leading-tight md:leading-5 text-fg-secondary truncate text-left'>
                        {file.original_filename}
                      </p>
                      {!isMobile && (
                        <div className='flex items-center gap-2 mt-1.5'>
                          <span className='text-sm leading-5 text-fg-tertiary'>
                            {(file.file_size / 1024).toFixed(0)} KB
                          </span>
                          <div className='w-px h-3 bg-border-secondary' />
                          <div className='flex items-center gap-1'>
                            <CheckCircle
                              className='size-4 text-utility-success-600 dark:text-utility-success-400'
                              strokeWidth={2}
                            />
                            <span className='text-sm font-medium leading-5 text-utility-success-600 dark:text-utility-success-400'>
                              100%
                            </span>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Delete button */}
                    <button
                      type='button'
                      data-file-id={file.id}
                      onClick={handleFileCardDelete}
                      className='shrink-0 p-1 hover:bg-bg-tertiary rounded-lg transition-colors cursor-pointer'
                      title='Remove file'
                      aria-label={`Remove ${file.original_filename}`}
                    >
                      <Trash01
                        className='size-3.5 md:size-4 text-fg-quaternary'
                        strokeWidth={2}
                      />
                    </button>
                  </div>
                );
              })}
            </>
          )}

          {/* Attached Files Chips - compact-style variants (simple + hero).
              Renders both successful uploads and in-flight/failed uploads so
              per-file errors stay attached to the file context (with retry)
              instead of falling through to the generic banner below. */}
          {isCompactStyle &&
            (uploadedFiles.length > 0 || pendingUploads.length > 0) && (
              <AttachedFilesChips
                files={uploadedFiles}
                pendingUploads={pendingUploads}
                onRemove={handleFileDelete}
                onRetry={handleRetryUpload}
                onDismissPending={handleDismissPendingUpload}
                onFocusFallback={focusEditor}
              />
            )}

          {/* Drive Connection Error Banner */}
          {driveConnectionError && (
            <div className='px-3 pb-3'>
              <div
                className='p-3 bg-utility-warning-50 dark:bg-utility-warning-900/20 border border-utility-warning-200 dark:border-utility-warning-800 rounded-lg'
                role='alert'
                aria-label='Google Drive connection error'
              >
                <div className='flex items-center justify-between gap-3'>
                  <div className='flex items-center gap-2'>
                    <svg
                      className='size-5 text-utility-warning-600 dark:text-utility-warning-400 shrink-0'
                      viewBox='0 0 20 20'
                      fill='currentColor'
                    >
                      <path
                        fillRule='evenodd'
                        d='M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z'
                        clipRule='evenodd'
                      />
                    </svg>
                    <span className='text-sm text-utility-warning-700 dark:text-utility-warning-300'>
                      Google Drive not connected
                    </span>
                  </div>
                  <div className='flex items-center gap-2'>
                    <button
                      type='button'
                      onClick={handleReconnectDrive}
                      className='px-3 py-1.5 text-xs font-medium text-white bg-utility-warning-600 hover:bg-utility-warning-700 rounded-md transition-colors cursor-pointer'
                    >
                      Connect Google Drive
                    </button>
                    <button
                      type='button'
                      onClick={handleDismissDriveError}
                      className='px-2 py-1.5 text-xs font-medium text-utility-warning-700 dark:text-utility-warning-300 hover:bg-utility-warning-100 dark:hover:bg-utility-warning-800/30 rounded-md transition-colors cursor-pointer'
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Error Display — generic banner for non-file errors (submission,
              voice, multi-file Drive import). Per-file upload failures render
              as error chips inside `AttachedFilesChips`. */}
          {(error || uploadError || voiceError) && !driveConnectionError && (
            <div className='px-3 pb-3'>
              <div
                className='p-2 bg-[var(--color-error-50)] dark:bg-[var(--color-error-900)]/20 border border-[var(--color-error-200)] dark:border-[var(--color-error-800)] rounded-lg'
                role='alert'
                aria-label='Message submission error'
              >
                <ErrorDisplay
                  error={error}
                  uploadError={uploadError}
                  voiceError={voiceError}
                  onDismissUploadError={handleDismissUploadError}
                />
              </div>
            </div>
          )}

          {/* ARIA live region for file attachment announcements */}
          <span role='status' aria-live='polite' className='sr-only'>
            {fileAnnouncement}
          </span>
        </form>
      </div>
    </div>
  );
}
